import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Confetti from '@/playlist/games/letterParty/components/gameBoard/Confetti';
import GridLines from '@/playlist/games/letterParty/components/gameBoard/GridLines';
import Hat from '@/playlist/games/letterParty/components/gameBoard/Hat';
import LetterCharacter from '@/playlist/games/letterParty/components/gameBoard/LetterCharacter';
import SnakeCharacter from '@/playlist/games/letterParty/components/gameBoard/SnakeCharacter';

export const GRID_WIDTH_PORTRAIT = 8;
export const GRID_HEIGHT_PORTRAIT = 12;
export const GRID_WIDTH_LANDSCAPE = 12;
export const GRID_HEIGHT_LANDSCAPE = 8;

const MIN_CELL_SIZE = 20;
const MAX_CELL_SIZE = 96;
const BOARD_PADDING = 32;

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

interface Position {
	x: number;
	y: number;
}

interface HatItem {
	id: number;
	position: Position;
	collected: boolean;
}

interface ConfettiItem {
	id: number;
	position: Position;
	collected: boolean;
}

interface LetterItem {
	letter: string;
	position: Position;
	collected: boolean;
	colorSeed: number;
}

interface GameBoardProps {
	gridWidth: number;
	gridHeight: number;
	isPortrait: boolean;
	isMobile: boolean;
	canRotateEntities: boolean;
	snake: Position[];
	currentDirection: Direction;
	hatCount: number;
	hats: HatItem[];
	confettis: ConfettiItem[];
	letterQs: LetterItem[];
	isRecovering: boolean;
	waitingForFirstDirection?: boolean;
	isStopped?: boolean;
	onRotateEntities?: (rotatePosition: (position: Position) => Position) => void;
}

export interface GameBoardHandle {
	getCellCenterPosition: (position: Position) => { x: number; y: number } | null;
}

const GameBoard = forwardRef<GameBoardHandle, GameBoardProps>(
	(
		{
			gridWidth,
			gridHeight,
			isPortrait,
			isMobile,
			canRotateEntities,
			snake,
			currentDirection,
			hatCount,
			hats,
			confettis,
			letterQs,
			isRecovering,
			waitingForFirstDirection = false,
			isStopped = false,
			onRotateEntities,
		},
		ref,
	) => {
		const [cellSize, setCellSize] = useState(45);
		const boardWrapperRef = useRef<HTMLDivElement | null>(null);
		const boardRef = useRef<HTMLDivElement | null>(null);
		const previousOrientationRef = useRef(isPortrait);

		const calculateCellSize = useCallback(() => {
			if (!boardWrapperRef.current) return;

			const wrapperRect = boardWrapperRef.current.getBoundingClientRect();
			const availableWidth = wrapperRect.width - BOARD_PADDING;
			const availableHeight = wrapperRect.height - BOARD_PADDING;

			const cellSizeByWidth = availableWidth / gridWidth;
			const cellSizeByHeight = availableHeight / gridHeight;
			const optimalCellSize = Math.min(cellSizeByWidth, cellSizeByHeight);
			const sanitizedOptimal = Number.isFinite(optimalCellSize)
				? optimalCellSize
				: MIN_CELL_SIZE;
			const clampedCellSize = Math.max(
				MIN_CELL_SIZE,
				Math.min(MAX_CELL_SIZE, sanitizedOptimal),
			);

			setCellSize(Math.max(MIN_CELL_SIZE, Math.floor(clampedCellSize)));
		}, [gridWidth, gridHeight]);

		useEffect(() => {
			calculateCellSize();
		}, [calculateCellSize, isMobile]);

		useEffect(() => {
			window.addEventListener('resize', calculateCellSize);
			return () => window.removeEventListener('resize', calculateCellSize);
		}, [calculateCellSize]);

		useEffect(() => {
			if (!onRotateEntities) return;
			if (!canRotateEntities) {
				previousOrientationRef.current = isPortrait;
				return;
			}

			if (previousOrientationRef.current === isPortrait) return;

			const oldIsPortrait = previousOrientationRef.current;
			previousOrientationRef.current = isPortrait;

			const oldWidth = oldIsPortrait ? GRID_WIDTH_PORTRAIT : GRID_WIDTH_LANDSCAPE;
			const oldHeight = oldIsPortrait ? GRID_HEIGHT_PORTRAIT : GRID_HEIGHT_LANDSCAPE;
			const newWidth = isPortrait ? GRID_WIDTH_PORTRAIT : GRID_WIDTH_LANDSCAPE;
			const newHeight = isPortrait ? GRID_HEIGHT_PORTRAIT : GRID_HEIGHT_LANDSCAPE;

			const rotatePosition = (pos: Position): Position => {
				let newX: number;
				let newY: number;

				if (isPortrait) {
					newX = oldHeight - 1 - pos.y;
					newY = pos.x;
				} else {
					newX = pos.y;
					newY = oldWidth - 1 - pos.x;
				}

				return {
					x: Math.max(0, Math.min(newWidth - 1, newX)),
					y: Math.max(0, Math.min(newHeight - 1, newY)),
				};
			};

			onRotateEntities(rotatePosition);
		}, [isPortrait, canRotateEntities, onRotateEntities]);

		useImperativeHandle(
			ref,
			() => ({
				getCellCenterPosition: (position: Position) => {
					if (!boardRef.current) return null;
					const boardRect = boardRef.current.getBoundingClientRect();
					return {
						x: boardRect.left + (position.x + 0.5) * cellSize,
						y: boardRect.top + (position.y + 0.5) * cellSize,
					};
				},
			}),
			[cellSize],
		);

		return (
			<div ref={boardWrapperRef} className="letter-party-game__board-wrapper">
				<div
					ref={boardRef}
					className="letter-party-game__board"
					style={{ width: gridWidth * cellSize, height: gridHeight * cellSize }}
				>
					<GridLines rows={gridHeight} cols={gridWidth} cellSize={cellSize} />

					{hats.map((hat) => (
						<Hat
							key={hat.id}
							position={hat.position}
							size={cellSize}
							collected={hat.collected}
						/>
					))}

					{confettis.map((confetti) => (
						<Confetti
							key={confetti.id}
							position={confetti.position}
							size={cellSize}
							collected={confetti.collected}
						/>
					))}

					{letterQs.map((letterQ, index) => (
						<LetterCharacter
							key={index}
							letter={letterQ.letter}
							size={cellSize}
							position={letterQ.position}
							collected={letterQ.collected}
							colorSeed={letterQ.colorSeed}
						/>
					))}

					{snake.map((segment, index) => {
						const showHatForSegment = index > 0 && index <= hatCount;
						const segmentAhead = index > 0 ? snake[index - 1] : undefined;
						const movingDirection =
							index === 0
								? currentDirection
								: segmentAhead
								? segmentAhead.x > segment.x
									? 'RIGHT'
									: segmentAhead.x < segment.x
									? 'LEFT'
									: segmentAhead.y > segment.y
									? 'DOWN'
									: 'UP'
								: currentDirection;

						const isMoving = !waitingForFirstDirection && !isStopped && !isRecovering;

						return (
							<SnakeCharacter
								key={`snake-${index}`}
								isLeader={index === 0}
								position={segment}
								size={cellSize}
								showHat={showHatForSegment}
								movingDirection={movingDirection}
								segmentIndex={index}
								animationSyncToken={snake.length + hatCount}
								isMoving={isMoving}
							/>
						);
					})}

					{isRecovering && <div className="letter-party-game__error-overlay" />}
				</div>
			</div>
		);
	},
);

export default GameBoard;
